'use client';

import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import {
  GETAPP_ORGANIZATION_TYPE_OPTIONS,
  GETAPP_PRICING_OPTIONS,
} from 'bx-forms/eas/getapp';
import useMediaQuery from 'hooks/useMediaQuery';

const ModalForm = dynamic(
  () => import('bx-forms/eas/getapp').then((mod) => mod.ModalForm),
  { ssr: false }
);

const GATEWAY_URL = 'https://www.capterra.com/bx-capterra-api/bx-forms/submit';

// Mobile breakpoint - matches GetApp's mobile breakpoint
const MOBILE_BREAKPOINT = '(max-width: 767px)';

// Time-on-page delay for mobile modals (110 seconds per Contentful config)
const MOBILE_TIME_ON_PAGE_DELAY_MS = 110000;

// Top-level regex patterns for URL matching (Biome: useTopLevelRegex)
const SPOTLIGHT_PAGE_REGEX = /\/[a-z0-9-]+-software\/a\/[a-z0-9-]+\/?/;
const CATEGORY_DETAIL_PAGE_REGEX = /\/[a-z0-9-]+-software\/[a-z0-9-]{2,}\/?$/;
const COMPARE_PAGE_REGEX = /\/[a-z0-9-]+-software\/a\/[a-z0-9-]+\/compare\//;

const isComparePage = (pathname) => COMPARE_PAGE_REGEX.test(pathname);

const isSpotlightPage = (pathname) =>
  SPOTLIGHT_PAGE_REGEX.test(pathname) && !isComparePage(pathname);

const isCategoryDetailPage = (pathname) => {
  return (
    CATEGORY_DETAIL_PAGE_REGEX.test(pathname) && !isSpotlightPage(pathname)
  );
};

/**
 * Checks if the current path is a SEM page.
 */
const isSemPage = (pathname) => {
  return pathname.includes('/p/sem/');
};

/**
 * Checks if exit intent should be disabled for this page.
 * Matches the GTM restricted URLs from Contentful config.
 */
const isExitIntentDisabled = (pathname) => {
  const restrictedPatterns = ['/x/', '/advisor-recommendations/'];
  return restrictedPatterns.some((pattern) => pathname.includes(pattern));
};

/**
 * SEM Exit Intent Modal
 * Shows on: SEM pages (/p/sem/*)
 */
const SemExitIntentModal = () => {
  const router = useRouter();
  const pathname = router.asPath.split('?')[0];

  // Only render on SEM pages
  if (!isSemPage(pathname) || isExitIntentDisabled(pathname)) {
    return null;
  }

  return (
    <ModalForm
      formId="4gqS79u64dxtc7lexNx5MF"
      gatewayUrl={GATEWAY_URL}
      trigger={{ type: 'exit-intent' }}
      displayFrequency="Once per user, ever"
      headline="Get the full software list now"
      subHeading="The latest prices and features straight to your inbox. Compare anytime."
      step2Subtitle="Thank you! Just one more question:"
      buttonText="Send me the list"
      buttonPlacement="stacked"
      secondButtonText="SUBMIT"
      successHeadline="Thank you, enjoy!"
      successMessage="Your list will be delivered to your inbox shortly."
      contextProps={{ pageType: 'sem', pagePlacement: 'modal' }}
      tracking={{
        formName: 'DMBUY_2022_GA_Blog_Acq',
        contentCategory: 'SEM_Desktop_Exit Intent',
        contentRequest: 'Product List',
      }}
      linkedFormIds={['7zZT0qEcjdnVRzMosFDYzb']}
    />
  );
};

/**
 * Generic Exit Intent Modal
 * Shows on: All GetApp pages
 * Blocked on: SEM pages, Spotlight pages, Category detail pages
 */
const GenericExitIntentModal = () => {
  const router = useRouter();
  // Extract pathname without query string
  const pathname = router.asPath.split('?')[0];

  // Don't render if on spotlight, category detail, or SEM pages
  if (
    isSpotlightPage(pathname) ||
    isCategoryDetailPage(pathname) ||
    isSemPage(pathname) ||
    isExitIntentDisabled(pathname)
  ) {
    return null;
  }

  return (
    <ModalForm
      formId="7zZT0qEcjdnVRzMosFDYzb"
      gatewayUrl={GATEWAY_URL}
      trigger={{ type: 'exit-intent' }}
      displayFrequency="Once per user, ever"
      headline="Top-rated software of 2026"
      subHeading="Fill out the form and we'll send a list of the top-ranked software based on real user reviews directly to your inbox."
      buttonText="Send me the list"
      buttonPlacement="stacked"
      successHeadline="Thank you, enjoy!"
      successMessage="Your guide will be delivered to your inbox shortly."
      contextProps={{ pageType: 'general', pagePlacement: 'modal' }}
      tracking={{
        formName: 'DMBUY_2022_GA_Blog_Acq',
        contentCategory: 'Exit Intent Modal',
        contentRequest: 'Product List',
      }}
      linkedFormIds={['QwVGyOVC2idLEeUFfC60Q']}
    />
  );
};

/**
 * Category Detail Exit Intent Modal
 * Shows on: Category detail pages (/{category}-software/{subcategory}/)
 * Blocked on: SEM pages, Spotlight pages
 * A/B Test: 5GjAWycIuSmEVdcGd1tU5v (using variant 14vg9IV8b8oecKWhulzLXX)
 */
const CategoryDetailExitIntentModal = () => {
  const router = useRouter();
  const pathname = router.asPath.split('?')[0];

  // Only render on category detail pages
  if (
    !isCategoryDetailPage(pathname) ||
    isSemPage(pathname) ||
    isExitIntentDisabled(pathname)
  ) {
    return null;
  }

  return (
    <ModalForm
      formId="14vg9IV8b8oecKWhulzLXX"
      gatewayUrl={GATEWAY_URL}
      trigger={{ type: 'exit-intent' }}
      displayFrequency="Once per user, ever"
      qualificationOptions={GETAPP_PRICING_OPTIONS}
      step2QuestionLabel="Which pricing model do you prefer?"
      headline="Get the full software list now"
      subHeading="The latest prices and features straight to your inbox. Compare anytime."
      step2Subtitle="Thank you! Let us know which pricing model you prefer and we'll send a list of tools with the right pricing available."
      buttonText="Send me the list"
      buttonPlacement="stacked"
      secondButtonText="SUBMIT"
      successHeadline="Thank you, enjoy!"
      successMessage="Your list will be delivered to your inbox shortly."
      contextProps={{ pageType: 'category', pagePlacement: 'modal' }}
      tracking={{
        formName: 'DMBUY_2022_GA_Blog_Acq',
        contentCategory: 'Directory_Desktop_Exit Intent',
        contentRequest: 'Product List',
      }}
      linkedFormIds={['7zZT0qEcjdnVRzMosFDYzb', 'QwVGyOVC2idLEeUFfC60Q']}
    />
  );
};

/**
 * Spotlight Exit Intent Modal
 * Shows on: Product detail pages (/{category}-software/a/{product}/)
 * Blocked on: SEM pages
 */
const SpotlightExitIntentModal = () => {
  const router = useRouter();
  // Extract pathname without query string
  const pathname = router.asPath.split('?')[0];

  // Only render on spotlight pages
  if (
    !isSpotlightPage(pathname) ||
    isSemPage(pathname) ||
    isExitIntentDisabled(pathname)
  ) {
    return null;
  }

  return (
    <ModalForm
      formId="QwVGyOVC2idLEeUFfC60Q"
      gatewayUrl={GATEWAY_URL}
      trigger={{ type: 'exit-intent' }}
      displayFrequency="Once per user, ever"
      qualificationOptions={GETAPP_PRICING_OPTIONS}
      step2QuestionLabel="Which pricing model do you prefer?"
      headline="Top-rated software of 2026"
      subHeading="Fill out the form and we'll send a list of the top-ranked software based on real user reviews directly to your inbox."
      step2Subtitle="You're in! To receive personalized recommendations, share your preferred pricing model."
      buttonText="Send me the list"
      buttonPlacement="stacked"
      successHeadline="Thank you, enjoy!"
      successMessage="Look out for future updates from GetApp."
      contextProps={{ pageType: 'spotlight', pagePlacement: 'modal' }}
      tracking={{
        formName: 'DMBUY_2022_GA_Blog_Acq',
        contentCategory: 'Spotlight_Exit Intent',
        contentRequest: 'Top Software',
      }}
      linkedFormIds={['7zZT0qEcjdnVRzMosFDYzb']}
    />
  );
};

/**
 * Spotlight Mobile Exit Intent Modal - Top Alternatives
 * Shows on: Spotlight pages on mobile devices
 * Trigger: Time on page (110 seconds)
 */
const SpotlightMobileExitModal = () => {
  const router = useRouter();
  const pathname = router.asPath.split('?')[0];
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  // Only render on spotlight pages on mobile
  if (
    !isMobile ||
    !isSpotlightPage(pathname) ||
    isSemPage(pathname) ||
    isExitIntentDisabled(pathname)
  ) {
    return null;
  }

  return (
    <ModalForm
      formId="6ukGgMJeYDPi6VJF5xC4X6"
      gatewayUrl={GATEWAY_URL}
      trigger={{ type: 'time-on-page', delayMs: MOBILE_TIME_ON_PAGE_DELAY_MS }}
      displayFrequency="Once per user, ever"
      qualificationOptions={GETAPP_ORGANIZATION_TYPE_OPTIONS}
      step2QuestionLabel="Which best describes your organization type?"
      headline="Send me personalized options for my organization"
      subHeading="We'll share a list of similar products tailored to your business."
      step2Subtitle="You're in! To receive personalized recommendations, share your organization type:"
      buttonText="SEND ME THE LIST"
      buttonPlacement="stacked"
      successHeadline="Thank you, enjoy!"
      successMessage="Your list will be delivered to your inbox shortly."
      contextProps={{
        pageType: 'spotlight',
        pagePlacement: 'modal',
        pageVariant: 'mobile',
      }}
      tracking={{
        formName: 'DMBUY_2022_GA_Blog_Acq',
        contentCategory: 'Spotlight Mobile_Exit Intent',
        contentRequest: 'Notify Me_Org Type',
      }}
      linkedFormIds={['QwVGyOVC2idLEeUFfC60Q', '7zZT0qEcjdnVRzMosFDYzb']}
    />
  );
};

/**
 * Directory Mobile Exit Intent Modal
 * Shows on: Category detail pages on mobile devices
 * Trigger: Time on page (110 seconds)
 */
const DirectoryMobileExitModal = () => {
  const router = useRouter();
  const pathname = router.asPath.split('?')[0];
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  // Only render on category detail pages on mobile
  if (
    !isMobile ||
    !isCategoryDetailPage(pathname) ||
    isSemPage(pathname) ||
    isExitIntentDisabled(pathname)
  ) {
    return null;
  }

  return (
    <ModalForm
      formId="6ykaZJ6LuumraNtPNYr7tX"
      gatewayUrl={GATEWAY_URL}
      trigger={{ type: 'time-on-page', delayMs: MOBILE_TIME_ON_PAGE_DELAY_MS }}
      displayFrequency="Once per user, ever"
      qualificationOptions={GETAPP_PRICING_OPTIONS}
      step2QuestionLabel="Which pricing model do you prefer?"
      headline="Get the full software list now"
      subHeading="The latest prices and features straight to your inbox. Compare anytime."
      step2Subtitle="Thank you! Let us know which pricing model you prefer and we'll send a list of tools with the right pricing available."
      buttonText="Send me the list"
      buttonPlacement="stacked"
      secondButtonText="SUBMIT"
      successHeadline="Thank you, enjoy!"
      successMessage="Your list will be delivered to your inbox shortly."
      contextProps={{
        pageType: 'category',
        pagePlacement: 'modal',
        pageVariant: 'mobile',
      }}
      tracking={{
        formName: 'DMBUY_2022_GA_Blog_Acq',
        contentCategory: 'Directory_Mobile_Exit Intent',
        contentRequest: 'Product List',
      }}
      linkedFormIds={['14vg9IV8b8oecKWhulzLXX', '7zZT0qEcjdnVRzMosFDYzb']}
    />
  );
};

/**
 * Spotlight Mobile Exit Intent Modal - Notify Me
 * Shows on: Spotlight pages on mobile devices (alternative variant)
 * Trigger: Time on page (110 seconds)
 * Note: Low traffic (237 views in 2025) - may be deprecated
 */
const SpotlightMobileNotifyModal = () => {
  const router = useRouter();
  const pathname = router.asPath.split('?')[0];
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  // Only render on spotlight pages on mobile
  // This modal competes with SpotlightMobileExitModal - linkedFormIds prevents both showing
  if (
    !isMobile ||
    !isSpotlightPage(pathname) ||
    isSemPage(pathname) ||
    isExitIntentDisabled(pathname)
  ) {
    return null;
  }

  return (
    <ModalForm
      formId="4n3qUl0KsYMXGwXirTeCqq"
      gatewayUrl={GATEWAY_URL}
      trigger={{ type: 'time-on-page', delayMs: MOBILE_TIME_ON_PAGE_DELAY_MS }}
      displayFrequency="Once per user, ever"
      qualificationOptions={GETAPP_ORGANIZATION_TYPE_OPTIONS}
      step2QuestionLabel="Which best describes your organization type?"
      headline="Send me pricing and feature updates"
      subHeading="We'll share when new pricing or feature updates are available for this product."
      step2Subtitle="You're in! To receive personalized recommendations, share your organization type:"
      buttonText="Notify me"
      buttonPlacement="stacked"
      successHeadline="Thank you, enjoy!"
      successMessage="Look out for future updates from GetApp."
      contextProps={{
        pageType: 'spotlight',
        pagePlacement: 'modal',
        pageVariant: 'mobile',
      }}
      tracking={{
        formName: 'DMBUY_2022_GA_Blog_Acq',
        contentCategory: 'Spotlight Mobile_Exit Intent',
        contentRequest: 'Notify Me',
      }}
      linkedFormIds={[
        '6ukGgMJeYDPi6VJF5xC4X6',
        'QwVGyOVC2idLEeUFfC60Q',
        '7zZT0qEcjdnVRzMosFDYzb',
      ]}
    />
  );
};

/**
 * Exit Intent Modals Container
 * Renders the appropriate exit intent modal based on current page.
 * Desktop modals use exit-intent trigger, mobile modals use time-on-page trigger.
 */
const ExitIntentModals = () => {
  return (
    <>
      {/* Desktop exit intent modals */}
      <GenericExitIntentModal />
      <CategoryDetailExitIntentModal />
      <SpotlightExitIntentModal />
      <SemExitIntentModal />
      {/* Mobile time-on-page modals */}
      <SpotlightMobileExitModal />
      <DirectoryMobileExitModal />
      <SpotlightMobileNotifyModal />
    </>
  );
};

export default ExitIntentModals;
